import { useState, useEffect } from "react";
import { useTenant } from "../../../hooks/useTenant";
import EvaluationService from "../../../services/evaluation_service";
import ActivityService from "../../../services/activity_service";

export const useKioskGamification = (member) => {
    const { idTenant } = useTenant();
    const [liveMember, setLiveMember] = useState(member);
    const [latestEvaluation, setLatestEvaluation] = useState(null);
    const [groupedCriteria, setGroupedCriteria] = useState({});
    const [gamifiedActivities, setGamifiedActivities] = useState([]);
    
    // Novas states para as conquistas gerais (ex: frequência)
    const [achievementsSettings, setAchievementsSettings] = useState([]);
    const [rankSettings, setRankSettings] = useState([]);
    const [awardedAchievementIds, setAwardedAchievementIds] = useState([]);
    const [awardedAchievements, setAwardedAchievements] = useState([]);
    
    // Níveis globais de avaliação
    const [levels, setLevels] = useState([]);
    
    const [loadingEval, setLoadingEval] = useState(true);
    const [activeTab, setActiveTab] = useState('avaliacao');

    useEffect(() => {
        if (!idTenant || !member?.id) {
            setLoadingEval(false);
            return;
        }

        let isMounted = true;
        setLoadingEval(true);

        const fetchGamificationData = async () => {
            try {
                // Import resources
                const GamificationService = (await import("../../../services/gamification_service")).default;
                const { collection, getDocs } = await import("firebase/firestore");
                const { db } = await import("../../../services/firebase");
                const MemberService = (await import("../../../services/member_service")).default;
                const { COLLECTIONS } = await import("../../../services/collections");

                // Fire parallel promises
                const LevelService = (await import("../../../services/level_service")).default;
                const [evaluations, allActivities, updatedMember, gamificationSettings, awardedSnapshot, allLevels] = await Promise.all([
                    EvaluationService.getAllByMember(idTenant, member.id),
                    ActivityService.getActivities(idTenant),
                    MemberService.getMemberById(idTenant, member.id),
                    GamificationService.getSettings(idTenant).catch(() => null), // Settings de gamificação
                    getDocs(collection(db, "tenants", idTenant, "members", member.id, COLLECTIONS.AWARDED_ACHIEVEMENTS)).catch(() => ({ docs: [] })),
                    LevelService.getLevels(idTenant).catch(() => [])
                ]);

                if (!isMounted) return;

                if (allLevels && allLevels.length > 0) {
                    setLevels(allLevels);
                }

                if (updatedMember) {
                    setLiveMember(updatedMember);
                }

                if (gamificationSettings?.achievements) {
                    setAchievementsSettings(gamificationSettings.achievements);
                }
                if (gamificationSettings?.levelRanks) {
                    setRankSettings(gamificationSettings.levelRanks);
                }

                if (awardedSnapshot?.docs) {
                    const awarded = awardedSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAwardedAchievements(awarded);
                    setAwardedAchievementIds(awarded.map(d => d.idAchievement || d.id));
                }

                // Filtra a trilha de Insígnias
                const evActivities = allActivities.filter(a => a.evaluationSystem === true).sort((a, b) => (a.order || 0) - (b.order || 0));
                setGamifiedActivities(evActivities);

                if (evaluations && evaluations.length > 0) {
                    const evalData = evaluations[0];
                    setLatestEvaluation(evalData);

                    let grouped = {};
                    if (evalData.objectives && Array.isArray(evalData.objectives)) {
                        evalData.objectives.forEach(obj => {
                            const objName = obj.name || "Geral";
                            grouped[objName] = obj.topics || [];
                        });
                    }
                    
                    setGroupedCriteria(grouped);
                }
            } catch (error) {
                console.error("Erro ao buscar avaliações e trilha do membro:", error);
            } finally {
                if (isMounted) setLoadingEval(false);
            }
        };

        fetchGamificationData();

        return () => {
            isMounted = false;
        };
    }, [idTenant, member]);

    return {
        liveMember,
        latestEvaluation,
        groupedCriteria,
        gamifiedActivities,
        achievementsSettings,
        rankSettings,
        awardedAchievementIds,
        awardedAchievements,
        loadingEval,
        activeTab,
        levels,
        actions: {
            setActiveTab
        }
    };
};
